import React from 'react';
import moment from 'moment';
import TaskBar from './TaskBar';
import EmptyCell from './EmptyCell';

interface Task {
  item_id: number;
  task_title: string;
  project_name: string;
  project_color: string;
  duration: number;
  completion?: number;
  start: string;
  end: string;
  project_id?: string;
}

interface GridCellProps {
  date: string;
  index: number;
  group: any;
  tasks: any;
  renderArr: string[];
  boxWidth: number;
  boxheight: number;
  searchText: string;
  orgWorkingDays: number[];
  weekdaysArray: string[];
  holidayNotifyList: string[];
  checkHoliday: (date1: any, date2: any, holidayNotifyList: any) => any[];
  areTasksOnSameDate: (tasks: Task[]) => boolean;
  onElementClick: (e: React.MouseEvent | null, ele: Task) => void;
  onDeleteClick: (e: React.MouseEvent | null, tasksData: Task, group: any) => void;
  onDateClick: (e: React.MouseEvent, clickedDate: string, group: any, tasksData: any, con: boolean) => void;
  isSingleTeamSelected?: boolean;
  isSprintFilter?: boolean;
}

const GridCell: React.FC<GridCellProps> = ({
  date,
  index,
  group,
  tasks,
  renderArr,
  boxWidth,
  boxheight,
  searchText,
  orgWorkingDays,
  weekdaysArray,
  holidayNotifyList,
  checkHoliday,
  areTasksOnSameDate,
  onElementClick,
  onDeleteClick,
  onDateClick,
  isSingleTeamSelected = false,
  isSprintFilter = false
}) => {
  const _curDate = date ? moment(date).format('YYYY-MM-DD') : "";
  const __curDate = new Date(date).setHours(0, 0, 0, 0);
  const today = new Date().setHours(0, 0, 0, 0);

  const tasksForDate: Task[] = (tasks[group.id]?.[_curDate] || []) as Task[];

  const dateValidate = moment(moment().format('YYYY-MM-DD')).diff(moment(`${date}`).format('YYYY-MM-DD'))
  const weekday = moment(_curDate).weekday();
  const isHoliday = checkHoliday(weekdaysArray[index], weekdaysArray[index + 1], holidayNotifyList);

  // Helper: compute how many visible working days a task spans from current date based on duration
  const computeSpanFromIndex = (task: Task): number => {
    const duration = Number(task?.duration || 1);
    let span = 0;

    // Count consecutive working days in renderArr starting from current index
    for (let i = index; i < renderArr.length && span < duration; i++) {
      const d = renderArr[i];
      if (d) { // Only count non-empty cells (working days)
        span++;
      }
    }

    return Math.max(span, 1);
  };

  // Determine which task to render in this cell
  let ongoingTask: Task | undefined;
  let shouldRenderBar = false;
  let barWidth = boxWidth;
  let barStyle: any = {};

  if (tasksForDate.length > 0) {
    // Find the task with the longest duration to determine bar width
    const longestTask = tasksForDate.reduce((longest: Task, t: Task) => {
      const longestDuration = Number(longest?.duration || 1);
      const currentDuration = Number(t?.duration || 1);
      return currentDuration > longestDuration ? t : longest;
    }, tasksForDate[0]);

    // Set the primary task for display (used when showing single task)
    ongoingTask = longestTask;

    // Always render the bar at its starting date
    shouldRenderBar = true;
    const span = computeSpanFromIndex(longestTask);
    barWidth = boxWidth * span;

    // Task always starts fresh at this date (left rounded)
    // Check if it ends within visible range (right rounded if it does)
    const taskDuration = Number(longestTask?.duration || 1);
    const isContinuedRight = span < taskDuration; // Task continues beyond visible range

    barStyle = {
      borderTopLeftRadius: '0.5rem',
      borderBottomLeftRadius: '0.5rem',
      borderTopRightRadius: isContinuedRight ? 0 : '0.5rem',
      borderBottomRightRadius: isContinuedRight ? 0 : '0.5rem',
    };
  }

  return (
    <div
      className={`relative inline-flex border-r
        hover:bg-gray-50 dark:hover:bg-gray-800/30 border-gray-200 dark:border-gray-500/10 border-l-[0.5px] 
        ${(weekday === orgWorkingDays.length && dateValidate !== 0) ? "!border-gray-500/30 !border-r-2" : ""} 
        ${isHoliday.length ? "!border-r-2 !border-r-blue-300" : ""}`
      }
      style={{
        width: boxWidth,
        minHeight: boxheight
      }}
    >
      {shouldRenderBar && ongoingTask ? (
        <TaskBar
          ongoingTask={ongoingTask}
          tasksForDate={tasksForDate}
          barWidth={barWidth}
          barStyle={barStyle}
          boxheight={boxheight}
          boxWidth={boxWidth}
          searchText={searchText}
          group={group}
          areTasksOnSameDate={areTasksOnSameDate}
          onElementClick={onElementClick}
          onDeleteClick={onDeleteClick}
          isSingleTeamSelected={isSingleTeamSelected}
          isSprintFilter={isSprintFilter}
        />
      ) : (
        <EmptyCell
          date={date}
          group={group}
          tasks={tasks}
          boxWidth={boxWidth}
          boxheight={boxheight}
          isPastDate={__curDate < today}
          orgWorkingDays={orgWorkingDays}
          onDateClick={onDateClick}
        />
      )}
    </div>
  );
};

export default GridCell; 